import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import { ClinicInfo } from 'generated/api';

/**
 * Temporary support for clinic closures that the backend has no concept of.
 *
 * A closed clinic keeps existing (patients are still attached to it, and its
 * past appointments still need a location), so it can't simply be hidden.
 * What it must not do is show an empty booking screen from the closure date
 * onwards: the patient is told the clinic is closed and handed a one-tap
 * switch to the replacement.
 *
 * Ids are listed per environment because staging and production don't share
 * them; the first id that actually exists in the loaded clinic list wins.
 *
 * Delete this file and its usages in AppointmentBooking/DateTime/SlotSchedule
 * once the closures below are old news.
 */
export interface ClinicClosure {
  /** Ids of the clinic that closes, across environments. */
  closedClinicIds: string[];
  /** Ids of the clinic to send patients to, across environments. */
  replacementClinicIds: string[];
  /** First closed day, YYYY-MM-DD, read in the clinic's timezone. */
  closedFrom: string;
}

const CLOSURES: ClinicClosure[] = [
  {
    // Care& Lawrence Park -> Care& Yorkville.
    closedClinicIds: [
      '4d5755f5-bce6-4d28-babb-320e90259a47', // production
      '4d109a14-0104-4093-a6e2-b246dbe68885', // staging
    ],
    replacementClinicIds: [
      'a510761a-2e9a-4da2-bbad-0ab31a6c2dc9', // production
      '61d6131e-cf3c-4018-822b-0633fa7c064b', // staging
    ],
    closedFrom: '2026-09-28',
  },
];

class ClinicClosures {
  /**
   * The closure covering `clinicId` on `date` (a unix timestamp), if any.
   * Dates before the closure return undefined, so the booking screen behaves
   * normally right up to the last open day.
   */
  getClosure(clinicId: string | undefined, date: number | undefined): ClinicClosure | undefined {
    if (!clinicId || !date) {
      return undefined;
    }

    const timezone = CalendarUtils.getTargetTimezone();

    return CLOSURES.find((closure) => {
      if (!closure.closedClinicIds.includes(clinicId)) {
        return false;
      }
      const closedFrom = moment.tz(closure.closedFrom, 'YYYY-MM-DD', timezone).startOf('day');
      return moment.unix(date).tz(timezone).isSameOrAfter(closedFrom, 'day');
    });
  }

  /**
   * Where `clinicId` effectively is on `date`: its replacement once closed,
   * itself otherwise. Practitioners keep the closed clinic on their profile
   * while their shifts move to the replacement, so any "does the primary work
   * at this clinic" comparison must resolve both sides through this.
   */
  resolveClinicId(
    clinicId: string | undefined,
    date: number | undefined,
    clinics: ClinicInfo[],
  ): string | undefined {
    const replacement = this.getReplacementClinic(this.getClosure(clinicId, date), clinics);
    return replacement?.id ?? clinicId;
  }

  /**
   * The replacement clinic, resolved against the clinics this environment
   * actually has. Returns undefined rather than guessing when none of the
   * configured ids are present.
   */
  getReplacementClinic(
    closure: ClinicClosure | undefined,
    clinics: ClinicInfo[],
  ): ClinicInfo | undefined {
    if (!closure) {
      return undefined;
    }
    return clinics.find(({ id }) => !!id && closure.replacementClinicIds.includes(id));
  }
}

export default new ClinicClosures();
